import { ACTING_AS_HEADER } from '@doorly/shared';
import { getActingAsUserId } from './actingAs';
import { getImpersonatedUserId } from './impersonation';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8080';

export interface ApiError extends Error {
  status: number;
  body: unknown;
}

/**
 * Redemande un JWT frais à Clerk, en court-circuitant son cache.
 *
 * Les tokens de session Clerk vivent ~60 s. Les appelants passent le token
 * qu'ils avaient sous la main au moment du render : une requête rejouée
 * depuis la file offline (Dexie), ou émise juste après un remount, peut
 * donc partir avec un jeton déjà périmé.
 *
 * Best-effort : si Clerk n'est pas monté ou refuse, on renvoie `null` et
 * l'appelant traite le 401 comme avant.
 */
async function freshToken(): Promise<string | null> {
  try {
    return (await window.Clerk?.session?.getToken?.({ skipCache: true })) ?? null;
  } catch {
    return null;
  }
}

/**
 * `actingAs` : au nom de quel vendeur la requête part (session PIN).
 * - `undefined` (défaut) → le vendeur actif de la tablette, s'il y en a un ;
 * - `null` → **aucun**, la requête est au niveau tablette (session du
 *   superviseur) — référentiels, vérificateurs, bascule ;
 * - une chaîne → ce vendeur précis, quel que soit l'actif — la file hors
 *   ligne rejoue chaque fiche sous l'identité de son auteur (F6).
 */
export interface RequestOptions {
  token?: string | null;
  idempotencyKey?: string;
  actingAs?: string | null;
}

async function requestJson<T>(
  method: string,
  path: string,
  opts: RequestOptions & { body?: unknown } = {},
): Promise<T> {
  const send = (token?: string | null) => {
    const headers: Record<string, string> = {};
    if (opts.body !== undefined && opts.body !== null) headers['Content-Type'] = 'application/json';
    if (token) headers.Authorization = `Bearer ${token}`;
    if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
    const imp = getImpersonatedUserId();
    if (imp) headers['X-Impersonate-As'] = imp;
    const acting = opts.actingAs === undefined ? getActingAsUserId() : opts.actingAs;
    if (acting) headers[ACTING_AS_HEADER] = acting;

    return fetch(`${API_URL}${path}`, {
      method,
      headers,
      body: opts.body == null ? undefined : JSON.stringify(opts.body),
    });
  };

  let res = await send(opts.token);

  // Un 401 sur un token périmé déconnectait l'utilisateur : `auth:unauthorized`
  // → `signOut()` dans App.tsx. Pour un vendeur sans adresse email (réseaux de
  // boutiques), une déconnexion fantôme n'est pas récupérable tout seul — il
  // faut que son manager lui redonne son mot de passe. On rejoue donc **une**
  // fois avec un token frais avant de conclure que la session est morte.
  //
  // Rejouer est sans risque même sans `Idempotency-Key` : un 401 vient du guard
  // d'authentification, donc la requête n'a produit aucun effet de bord côté
  // serveur.
  if (res.status === 401) {
    const retryToken = await freshToken();
    if (retryToken && retryToken !== opts.token) {
      res = await send(retryToken);
    }
  }

  if (!res.ok) {
    if (res.status === 401) {
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      body = await res.text().catch(() => '');
    }
    const err = new Error(
      typeof body === 'object' && body !== null && 'message' in body
        ? String((body as { message: unknown }).message)
        : `${res.status} ${res.statusText}`,
    ) as ApiError;
    err.status = res.status;
    err.body = body;
    throw err;
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const apiClient = {
  get: <T>(path: string, token?: string | null, opts: Omit<RequestOptions, 'token'> = {}) =>
    requestJson<T>('GET', path, { ...opts, token }),
  post: <T>(path: string, body: unknown, opts: RequestOptions = {}) =>
    requestJson<T>('POST', path, { ...opts, body }),
  patch: <T>(path: string, body: unknown, opts: RequestOptions = {}) =>
    requestJson<T>('PATCH', path, { ...opts, body }),
  put: <T>(path: string, body: unknown, opts: RequestOptions = {}) =>
    requestJson<T>('PUT', path, { ...opts, body }),
  delete: <T = void>(path: string, token?: string | null, opts: Omit<RequestOptions, 'token'> = {}) =>
    requestJson<T>('DELETE', path, { ...opts, token }),
};
